import { isPlatformBrowser, Location } from '@angular/common';
import type { ErrorHandler } from '@angular/core';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { EnvService } from '@core-mkt/services/env/env.service';
import type { ConsoleData } from './error-handler';

import type { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import type { Observable } from 'rxjs';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import type { LogError } from '../../../shared/lib/logger';
import { logError, SEVERITY } from '../../../shared/lib/logger';

@Injectable({
  providedIn: 'root',
})
export class ServerErrorHandlerService implements ErrorHandler, HttpInterceptor {
  visionData: ConsoleData;
  private readonly enableRequestingUriHeader: boolean;
  private readonly requestingUriTargets: string[];

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private location: Location,
    private env: EnvService,
  ) {
    const envData = this.env.get;
    const path = this.location.path();
    const slug = path.substring(1, path.length - 1);

    this.visionData = {
      appParams: envData.xgritApiConfig.baseParams,
      urlPath: path,
      craft: {
        slug: slug.replace(/\//g, '--'),
        site: envData.brandConfig.craftSite,
      },
    };

    this.enableRequestingUriHeader = envData.enableRequestingUriHeader ?? false;
    this.requestingUriTargets = [envData.xgritApiConfig.url, envData.craftApiUrl, envData.blogCraftApiUrl].filter(
      Boolean,
    );
  }

  /**
   * This is an interceptor that handles HTTP errors and enables distributed tracing context.
   *
   * For each outgoing HTTP request, this interceptor:
   * 1. Sets the requesting URI in Sentry's scope for Sentry-instrumented backends
   * 2. Adds a custom X-Requesting-URI header for OpenTelemetry-instrumented backends
   *
   * ## Why Both Approaches?
   *
   * **Sentry Tags (setTag):**
   * - Attaches the requesting URI to Sentry events/transactions
   * - Works for backends instrumented with Sentry SDK
   * - However, custom tags set via setTag() are NOT automatically added to the W3C baggage header
   *
   * **Custom HTTP Header (X-Requesting-URI):**
   * - Provides a reliable way to pass the requesting URI across service boundaries
   * - Works for OpenTelemetry-instrumented backends that don't use Sentry SDK
   * - OTEL services can extract this header and add it to their trace context
   * - Avoids the limitations of Sentry's Dynamic Sampling Context (DSC) which only
   *   propagates predefined fields (trace_id, environment, release, transaction, etc.)
   *
   * ## OpenTelemetry Compatibility
   *
   * Sentry's baggage header uses the W3C spec but is primarily for Sentry's Dynamic Sampling
   * Context. Custom tags don't automatically propagate to OTEL services. The custom header
   * ensures compatibility in mixed instrumentation scenarios (Sentry frontend → OTEL backend).
   *
   * Related Sentry config: `propagateTraceparent: true` and `tracePropagationTargets` in main.ts
   *
   * @param request The request to intercept.
   * @param next The next `HttpHandler` in the chain.
   * @returns The `Observable` of the `HttpEvent`.
   *
   * @see https://develop.sentry.dev/sdk/performance/dynamic-sampling-context/
   * @see https://develop.sentry.dev/sdk/telemetry/traces/opentelemetry/
   * @see https://www.w3.org/TR/baggage/
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only set requesting URI in browser context (not during SSR)
    if (isPlatformBrowser(this.platformId)) {
      // Set the requesting URI in Sentry scope for Sentry-instrumented backends
      // This will be included in Sentry events but NOT in the baggage header
      Sentry.getCurrentScope().setTag('requesting_uri', window.location.href);

      // Add custom header for reliable propagation to OpenTelemetry-instrumented backends.
      // Scoped to known API targets (xgritApiConfig.url, craftApiUrl, blogCraftApiUrl) to avoid
      // CORS issues with services that haven't allowlisted this header. Enable via
      // environment.enableRequestingUriHeader once the target APIs' CORS configs are updated.
      const isTargetedApi =
        this.enableRequestingUriHeader && this.requestingUriTargets.some((host) => request.url.startsWith(host));

      if (isTargetedApi) {
        return next
          .handle(
            request.clone({
              setHeaders: { 'X-Requesting-URI': window.location.href },
            }),
          )
          .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
      }
    }

    // SSR, feature disabled, or non-targeted URL: pass through without modification
    return next.handle(request).pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * handleError catches any unhandled runtime error on the server side and formats it for Coralogix
   *
   * @param {object} error - Native error throwned by Angular
   */
  handleError(error: any): void {
    logError({
      error: error as LogError,
      severity: SEVERITY.ERROR,
      params: error.appParams as string[],
      urlPath: error.urlPath as string,
      craft: error.craft as string[],
      message: this.getStatusMessage(error?.status),
      tags: ['ServerErrorHandlerService.handleError'],
    });
  }

  /**
   * get context based on status code. This is used to determine if the error is a runtime error or http error
   *
   * @param {number} status - status code
   */
  private getStatusMessage = (status) => {
    switch (status) {
      case undefined:
        return 'Runtime JS';
      case 0:
        // Check if url request exists or CORS error
        return 'Http Preflight';
      default:
        // Please look at the nativeErr property for more details
        return 'Http Native';
    }
  };
}
